/**
 * CEODashboardPage — Executive-level, single-screen view of liquidity health.
 * Designed for board and executive audiences with large, scannable KPIs,
 * traffic-light status, and an ending-cash trend line.
 */

import React, { useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useActionSummary, useFacilityConfig } from './api';
import { formatCurrency, formatPct, liquidityBg, covenantBg, computeKPIs, formatWeekLabel } from './utils';
import { cn } from '../../shared/lib/cn';

function BigKPI({ label, value, accent, sub }: { label: string; value: string; accent?: string; sub?: string }) {
  return (
    <div className="text-center">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={cn('text-3xl font-bold', accent || 'text-charcoal dark:text-gray-100')}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function CEODashboardPage() {
  const { data: summary, isLoading } = useForecastSummary();
  const { data: actionSummary } = useActionSummary();
  const { data: facility } = useFacilityConfig();

  const kpis = useMemo(() => (summary?.weeks ? computeKPIs(summary.weeks) : null), [summary]);
  const weeks = summary?.weeks ?? [];

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  }

  if (!kpis || !weeks.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          No forecast data available. Generate a forecast first.
        </CardContent>
      </Card>
    );
  }

  const lastWeek = weeks[weeks.length - 1];
  const currentStatus = lastWeek.liquidity_status;

  return (
    <div className="space-y-6 mt-2">
      {/* Hero status */}
      <Card>
        <CardContent className="py-8 px-6">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className={cn('px-4 py-1.5 rounded-full text-sm font-semibold', liquidityBg(currentStatus))}>
              Liquidity: {currentStatus}
            </span>
            <span className={cn('px-4 py-1.5 rounded-full text-sm font-semibold', covenantBg(lastWeek.covenant_status))}>
              Covenant: {lastWeek.covenant_status}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <BigKPI
              label="Current Cash"
              value={formatCurrency(weeks[0].beginning_cash, true)}
            />
            <BigKPI
              label="Ending Cash W13"
              value={formatCurrency(kpis.ending_cash_week13, true)}
              accent={kpis.ending_cash_week13 < 12_000_000 ? 'text-red-600' : undefined}
            />
            <BigKPI
              label="Min Cash"
              value={formatCurrency(kpis.minimum_cash_amount, true)}
              sub={`Week ${kpis.minimum_cash_week}`}
              accent={kpis.minimum_cash_amount < 12_000_000 ? 'text-red-600' : undefined}
            />
            <BigKPI
              label="Runway"
              value={`${kpis.weeks_of_runway > 26 ? '26+' : kpis.weeks_of_runway.toFixed(1)} wks`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Trend + Detail panels */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Ending Cash Trend — left 25% */}
        <div className="lg:col-span-1">
          <Card className="h-full">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-3">
                Ending Cash Trend
              </h3>
              <div className="space-y-1.5">
                {weeks.map(w => {
                  const maxCash = Math.max(...weeks.map(x => x.ending_cash));
                  const pct = maxCash > 0 ? (w.ending_cash / maxCash) * 100 : 0;
                  return (
                    <div key={w.week_number} className="flex items-center gap-2">
                      <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 w-6 text-right shrink-0">
                        W{w.week_number}
                      </span>
                      <div className="flex-1 h-4 bg-gray-100 dark:bg-dark-surface-2 rounded overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded transition-all',
                            w.liquidity_status === 'RED' ? 'bg-red-400' :
                            w.liquidity_status === 'YELLOW' ? 'bg-amber-400' : 'bg-emerald-400',
                          )}
                          style={{ width: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-gray-400 tabular-nums w-12 text-right shrink-0">
                        {formatCurrency(w.ending_cash, true)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Facility & Actions — right 75% */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-3">Facility Position</h3>
              {facility ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Total Commitment</span>
                    <span className="font-medium">{formatCurrency(facility.total_commitment, true)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Outstanding</span>
                    <span className="font-medium">{formatCurrency(facility.current_outstanding, true)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Available</span>
                    <span className="font-medium text-emerald-600">
                      {formatCurrency(facility.total_commitment - facility.current_outstanding, true)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Utilization</span>
                    <span className="font-medium">
                      {facility.total_commitment > 0
                        ? formatPct((facility.current_outstanding / facility.total_commitment) * 100)
                        : '0%'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No facility configured</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-3">Action Items</h3>
              {actionSummary ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Open</span>
                    <span className="font-medium">{actionSummary.total_open}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">In Progress</span>
                    <span className="font-medium">{actionSummary.total_in_progress}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Overdue</span>
                    <span className={cn('font-medium', actionSummary.total_overdue > 0 ? 'text-red-600' : '')}>
                      {actionSummary.total_overdue}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Realized Impact</span>
                    <span className="font-medium text-emerald-600">
                      {formatCurrency(actionSummary.realized_cash_impact, true)}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Loading...</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
